import { useEffect, useMemo, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { fetchAuthSession } from "@aws-amplify/auth";
import { signIn } from "../../sso/config";
import {
  getClaimsAdminLoginUrl,
  getClaimsOrigin,
  normalizeClaimsAdminPortal,
} from "../../utils/claimsPortal";
import logo from "../../assets/images/logo.svg";
import claimsHero from "../../assets/images/admin-bg-claims.jpg";
import ifasHero from "../../assets/images/admin-bg-ifas.jpg";

const portalContent = {
  claims: {
    title: "Claims admin login",
    description: "Sign in to the Claims administration portal.",
    button: "Login",
    image: claimsHero,
  },
  ifas: {
    title: "Funerals admin login",
    description: "Sign in to the Funerals administration portal.",
    button: "Login",
    image: ifasHero,
  },
};

export default function AdminLoginPage() {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState("");
  const [isRedirecting, setIsRedirecting] = useState(false);

  const portal = useMemo(
    () => normalizeClaimsAdminPortal(searchParams.get("portal")),
    [searchParams]
  );

  const content = portalContent[portal];

  // If we ever land on the wrong origin, bounce to the canonical claims origin
  // so the Cognito callback URLs stay consistent.
  useEffect(() => {
    const claimsOrigin = getClaimsOrigin();

    if (claimsOrigin && window.location.origin !== claimsOrigin) {
      window.location.replace(getClaimsAdminLoginUrl(portal));
    }
  }, [portal]);

  const handleLogin = async () => {
    setError("");
    setIsRedirecting(true);

    try {
      localStorage.setItem("selectedPortal", portal);
      await signIn();
    } catch (signInError) {
      console.error("Unable to start admin login", signInError);

      // `clearClientSession()` (services/session.ts) tears down this app's own
      // stores but never signs Amplify out, so the two can disagree: the UI
      // shows "signed out" while Amplify still holds a live session. The next
      // `signInWithRedirect()` then throws UserAlreadyAuthenticatedException
      // and the user is stuck on this page with no way forward, because the
      // one control here is the button that just failed.
      if ((signInError as any)?.name === "UserAlreadyAuthenticatedException") {
        try {
          const session = await fetchAuthSession();
          if (session.tokens?.accessToken) {
            // The session is real and usable — honour it instead of forcing a
            // second login. /claims is the OAuth callback route, which resolves
            // the user and lands on the dashboard exactly as a fresh login does.
            window.location.assign("/claims");
            return;
          }
        } catch {
          /* fall through to the reset below */
        }

        // Amplify claims a user but cannot produce tokens: the session is
        // stale. Drop Amplify's own keys and reload so it re-reads clean
        // storage, which makes the next click a normal first-time login.
        try {
          Object.keys(localStorage)
            .filter(
              (key) =>
                key.startsWith("CognitoIdentityServiceProvider.") ||
                key.toLowerCase().startsWith("amplify"),
            )
            .forEach((key) => localStorage.removeItem(key));
          sessionStorage.clear();
        } catch {
          /* storage unavailable */
        }
        window.location.reload();
        return;
      }

      // Outside production, surface the underlying Amplify exception. The
      // generic message alone makes a failed `signInWithRedirect()` impossible
      // to diagnose without opening DevTools, and the cause (stale OAuth
      // state, an origin that matches no registered redirect URL, an already
      // authenticated user) needs completely different fixes.
      const detail =
        process.env.NODE_ENV !== "production" && signInError
          ? ` [${(signInError as any)?.name || "Error"}: ${
              (signInError as any)?.message || String(signInError)
            }]`
          : "";
      setError(`Unable to redirect to login. Please try again.${detail}`);
      setIsRedirecting(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", bgcolor: "#FFFFFF" }}>
      {/* Left: brand photo (hidden on small screens) */}
      <Box
        aria-hidden
        sx={{
          display: { xs: "none", md: "block" },
          flexBasis: "50%",
          position: "relative",
          backgroundImage: `url(${content.image})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to bottom, rgba(0,0,0,0.12), rgba(0,0,0,0.28))",
          }}
        />
      </Box>

      {/* Right: sign-in column */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            flex: 1,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            px: { xs: 3, md: 6 },
            py: 6,
          }}
        >
        <Box
          component="img"
          src={logo}
          alt="Icebolethu Group"
          sx={{
            height: { xs: 52, md: 60 },
            width: "auto",
            maxWidth: 230,
            objectFit: "contain",
            mb: 3,
          }}
        />

        <Typography
          component="h1"
          sx={{
            fontSize: { xs: 24, md: 28 },
            lineHeight: 1.2,
            fontWeight: 800,
            color: "#1A1F2E",
          }}
        >
          {content.title}
        </Typography>

        <Typography
          sx={{
            color: "#5F6676",
            fontSize: 14,
            lineHeight: 1.6,
            mt: 1.5,
            maxWidth: 320,
          }}
        >
          {content.description}
        </Typography>

        <Button
          variant="contained"
          size="large"
          onClick={handleLogin}
          disabled={isRedirecting}
          sx={{
            mt: 3,
            minWidth: 220,
            bgcolor: "#EC6E00",
            color: "#FFFFFF",
            textTransform: "none",
            fontWeight: 700,
            borderRadius: 1.5,
            py: 1.25,
            boxShadow: "none",
            "&:hover": { bgcolor: "#D86300", boxShadow: "none" },
          }}
        >
          {isRedirecting ? (
            <Box
              component="span"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <CircularProgress size={18} color="inherit" />
              Redirecting...
            </Box>
          ) : (
            content.button
          )}
        </Button>

        {error && (
          <Typography
            role="alert"
            sx={{ color: "#B42318", fontSize: 13, fontWeight: 600, mt: 2 }}
          >
            {error}
          </Typography>
        )}
        </Box>

        <Box
          component="footer"
          sx={{
            width: "100%",
            borderTop: "1px solid rgba(15,23,42,0.08)",
            px: { xs: 3, md: 6 },
            pt: 2,
            pb: 4,
            textAlign: "center",
            color: "#5F6676",
            fontSize: 13,
            lineHeight: 1.7,
          }}
        >
          <Box>
            <Box component="span" sx={{ fontWeight: 700, color: "#1A1F2E" }}>
              Call Centre:
            </Box>{" "}
            086 000 7580
          </Box>
          <Box>
            <Box component="span" sx={{ fontWeight: 700, color: "#1A1F2E" }}>
              Email:
            </Box>{" "}
            info@icebolethu.co.za
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
